"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.rotateFocusPoint = exports.rotateCropRectangle = exports.normalizeUrl = exports.getImageSourceProps = exports.getImageAspectRatio = exports.getFittingRect = exports.convertFocusPointToCss = exports.centerCoordinate = exports.applySizeLimit = exports.applyMetadata = void 0;
var _clamp = _interopRequireDefault(require("lodash/clamp"));
var _hostUtils = require("./host-utils");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
var DEFAULT_SOURCE_SET_SIZES = [400, 800, 1200, 1600];
var DEFAULT_SOURCE_SIZE = 400;

/**
 * Computes dimensions and position of a child needed for it to fit the parent.
 *
 * Works exactly as `background-position` and `object-position` do in CSS.
 *
 * Example of `kx` an `ky` values: if the desired position is 50% by 75%, you
 * should provide 0.5 and 0.75 as their values.
 *
 * @param {string} fit              How the child should be resized to fit the parent ('cover' or 'contain').
 * @param {string} kx               How the child should be positioned to fit the parent horizontally.
 * @param {string} ky               How the child should be positioned to fit the parent vertically.
 * @param {number} parentWidth      The width of the parent.
 * @param {number} parentHeight     The height of the parent.
 * @param {number} childAspectRatio The aspect ratio of the child.
 *
 * @return {Object} Computed dimensions and position of the child.
 */
var getFittingRect = exports.getFittingRect = function getFittingRect(fit, kx, ky, parentWidth, parentHeight, childAspectRatio) {
  var containerAspectRatio = parentWidth / parentHeight;
  if (fit === 'cover') {
    if (containerAspectRatio > childAspectRatio) {
      var _childHeight = parentWidth / childAspectRatio;
      return {
        width: parentWidth,
        height: _childHeight,
        x: 0,
        y: ky * (parentHeight - _childHeight)
      };
    }
    var childWidth = parentHeight * childAspectRatio;
    return {
      width: childWidth,
      height: parentHeight,
      x: kx * (parentWidth - childWidth),
      y: 0
    };
  }
  if (containerAspectRatio > childAspectRatio) {
    var _childWidth = parentHeight * childAspectRatio;
    return {
      width: _childWidth,
      height: parentHeight,
      x: kx * (parentWidth - _childWidth),
      y: 0
    };
  }
  var childHeight = parentWidth / childAspectRatio;
  return {
    width: parentWidth,
    height: childHeight,
    x: 0,
    y: ky * (parentHeight - childHeight)
  };
};

/**
 * Generates the src and srcSet properties for an image with transformations
 * from its metadata applied.
 *
 * @param {number} containerAspectRatio The aspect ratio of the image's container.
 * @param {number} [imageAspectRatio]   The aspect ratio of the image.
 * @param {number} [url]                The original URL of the image.
 * @param {Object} [metadata]           The image's metadata.
 *
 * @return {?Object} An object containing the src and srcSet properties,
 *                   or null if url was not provided.
 */
var getImageSourceProps = exports.getImageSourceProps = function getImageSourceProps(containerAspectRatio, imageAspectRatio, url, metadata) {
  var normalizedUrl = normalizeUrl(url);
  var limitBy;
  if (!url) {
    return null;
  }
  if (imageAspectRatio) {
    var shouldScaleToFit = Boolean(metadata && metadata.contain);
    limitBy = containerAspectRatio < imageAspectRatio === shouldScaleToFit ? 'width' : 'height';
  } else {
    limitBy = 'width';
  }
  var urlWithTransformations = applyMetadata(normalizedUrl, metadata, imageAspectRatio);
  return {
    src: applySizeLimit(urlWithTransformations, _defineProperty({}, limitBy, DEFAULT_SOURCE_SIZE)),
    srcSet: DEFAULT_SOURCE_SET_SIZES.map(function (size) {
      var url = applySizeLimit(urlWithTransformations, _defineProperty({}, limitBy, size));
      var width = Math.round(limitBy === 'width' ? size : size / containerAspectRatio);
      return "".concat(url, " ").concat(width, "w");
    }).join(', ')
  };
};

/**
 * Computes the expected image aspect ratio.
 *
 * @param {Object} crop         The crop rectangle from metadata.
 * @param {number} [rotation=0] The rotation amount in degrees.
 *
 * @return {number} The expected aspect ratio.
 */
var getImageAspectRatio = exports.getImageAspectRatio = function getImageAspectRatio(_ref) {
  var w = _ref.w,
    h = _ref.h;
  var rotation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  if (rotation === 90 || rotation === 270) {
    return h / w;
  }
  return w / h;
};

/**
 * Rotates the crop rectange from metadata.
 *
 * @param {Object} crop           The crop rectangle from metadata.
 * @param {number} originalWidth  The width of the original image.
 * @param {number} originalHeight The height of the original image.
 * @param {number} [rotation=0]   The rotation amount in degrees.
 *
 * @return {Object} A new crop rectangle with the rotation applied.
 */
var rotateCropRectangle = exports.rotateCropRectangle = function rotateCropRectangle(_ref2, originalWidth, originalHeight) {
  var x = _ref2.x,
    y = _ref2.y,
    w = _ref2.w,
    h = _ref2.h;
  var rotation = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  if (rotation === 90) {
    return {
      x: y,
      y: originalHeight - x - w,
      w: h,
      h: w
    };
  }
  if (rotation === 180) {
    return {
      x: originalWidth - x - w,
      y: originalHeight - y - h,
      w: w,
      h: h
    };
  }
  if (rotation === 270) {
    return {
      x: originalWidth - y - h,
      y: x,
      w: h,
      h: w
    };
  }
  return {
    x: x,
    y: y,
    w: w,
    h: h
  };
};

/**
 * Rotates the focus point from metadata.
 *
 * @param {Object}  focus            The focus point from metadata.
 * @param {number}  [rotation=0]     The rotation amount in degrees.
 * @param {boolean} [clockwise=true] Whether the rotation should be clockwise.
 *
 * @return {Object} A new focus point with the rotation applied.
 */
var rotateFocusPoint = exports.rotateFocusPoint = function rotateFocusPoint(_ref3) {
  var kx = _ref3.kx,
    ky = _ref3.ky;
  var rotation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var clockwise = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  rotation = clockwise ? rotation : 360 - rotation;
  if (rotation === 90) {
    return {
      kx: 1 - ky,
      ky: kx
    };
  }
  if (rotation === 180) {
    return {
      kx: 1 - kx,
      ky: 1 - ky
    };
  }
  if (rotation === 270) {
    return {
      kx: ky,
      ky: 1 - kx
    };
  }
  return {
    kx: kx,
    ky: ky
  };
};

/**
 * Finds a background-position / object-position value that will center the provided coordinate.
 *
 * @param {number} k     The relative coordinate on the image's scale.
 * @param {number} scale How many times the image's scale is larger than the container's one.
 *
 * @return {number} The relative background-position / object-position value.
 */
var centerCoordinate = exports.centerCoordinate = function centerCoordinate(k, scale) {
  if (scale === 1) {
    return 0.5;
  }
  return (0, _clamp["default"])((k * scale - 0.5) / (scale - 1), 0, 1);
};

/**
 * Converts the focus point to background-position / object-position coordinates that center it.
 *
 * @param {number} containerAspectRatio The aspect ratio of the image's container.
 * @param {number} imageAspectRatio     The aspect ratio of the image.
 * @param {Object} [metadata]           The image's metadata.
 *
 * @return {Object} A new focus point with the conversion applied.
 */
var convertFocusPointToCss = exports.convertFocusPointToCss = function convertFocusPointToCss(containerAspectRatio, imageAspectRatio, metadata) {
  var convertedFocus = {
    kx: 0.5,
    ky: 0.5
  };
  if (!metadata || !metadata.focus || metadata.contain) {
    return convertedFocus;
  }
  var _rotateFocusPoint = rotateFocusPoint(metadata.focus, metadata.rotation),
    kx = _rotateFocusPoint.kx,
    ky = _rotateFocusPoint.ky;
  if (containerAspectRatio > imageAspectRatio) {
    convertedFocus.ky = centerCoordinate(ky, containerAspectRatio / imageAspectRatio);
  } else {
    convertedFocus.kx = centerCoordinate(kx, imageAspectRatio / containerAspectRatio);
  }
  return convertedFocus;
};

/**
 * Constructs the URL of an image with size limitations applied.
 *
 * @param {string} url             The original URL of the image.
 * @param {Object} [limits]
 * @param {number} [limits.width]  The maximum width.
 * @param {number} [limits.height] The maximum width.
 *
 * @return {?string} A new URL with transformations applied
 *                   or null if no url was provided.
 */
var applySizeLimit = exports.applySizeLimit = function applySizeLimit(url) {
  var _ref4 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    width = _ref4.width,
    height = _ref4.height;
  if (!url) {
    return null;
  }
  var params = [];
  if (width) {
    params.push("w_".concat(width));
  }
  if (height) {
    params.push("h_".concat(height));
  }
  if (params.length) {
    return url.replace(/(\/v\d)/, "/".concat(params.join(','), ",c_limit/f_auto,q_auto:best$1"));
  }
  return url;
};

/**
 * Constructs the URL of an image with transformations from its metadata applied.
 *
 * @param {number} [url]      The original URL of the image.
 * @param {Object} [metadata] The image's metadata.
 *
 * @return {?string} A new URL with transformations applied,
 *                   or null if no url was provided.
 */
var applyMetadata = exports.applyMetadata = function applyMetadata(url, metadata, imageAspectRatio) {
  if (!url) {
    return null;
  }
  if (!metadata) {
    return url;
  }
  var isSvg = url.slice(-4) === '.svg';
  var transformations = [];
  var crop = metadata.crop,
    rotation = metadata.rotation,
    contain = metadata.contain;
  if (crop && !isSvg) {
    var x = crop.x,
      y = crop.y,
      w = crop.w,
      h = crop.h;
    transformations.push("x_".concat(x, ",y_").concat(y, ",w_").concat(w, ",h_").concat(h, ",c_crop"));
  }
  transformations.push("a_".concat(rotation ? rotation : 'exif'));
  if (contain) {
    transformations.push("ar_".concat(imageAspectRatio || 4 / 3, ",c_pad"));
  }
  if (transformations.length) {
    return url.replace('/upload/', "/upload/".concat(transformations.join('/'), "/"));
  }
  return url;
};

/**
 * I don't know what this is and if it's ever needed. Just a straight copy-paste
 * from the previous version.
 * - @borodean
 */
var normalizeUrl = exports.normalizeUrl = function normalizeUrl(urlString) {
  try {
    var url = new URL(urlString);
    if (!url.href.includes('/upload/')) {
      return null;
    }
    url.protocol = 'https:';
    url.hostname = (0, _hostUtils.localiseHostName)(url.hostname);
    url.pathname = url.pathname.replace(/\.mov|\.mp4/gi, '.jpg');
    return url.href;
  } catch (e) {
    return null;
  }
};
//# sourceMappingURL=image-utils.js.map